import { usePartnerAccess } from '@/hooks/usePartnerAccess';
import { getPartnerDrawerUrl } from '@/services/partner-links';
import {
  SMS_LOGS_PHONE_QUERY_PARAM,
  TIMELINE_FOCUS_ITEM_QUERY_PARAM,
  TaskGroupIdentifier,
  TIMELINE_INFO_TAB_QUERY_PARAM,
} from '@bariendo/lib/constants';
import { getExternalNumberFromPhoneCommunication, isFaxCommunication } from '@bariendo/lib/communication';
import { isReferenceOf } from '@bariendo/lib/services';
import { getExtensionValue, getReferenceString, resolveId } from '@medplum/core';
import { Communication } from '@medplum/fhirtypes';
import { useNavigate } from 'react-router';
import { NotificationItem } from './types';
import { FAX_ORIGINATING_NUMBER } from '@bariendo/lib/extensions';
import { FAX_SEARCH_QUERY_PARAM } from '@/pages/FaxingPage/FaxingPage';
import { isUnknownNumberSmsNotification } from './services';

export function useNotificationNavigation() {
  const navigate = useNavigate();
  const hasPartnerAccess = usePartnerAccess();

  const handleRowClick = async (notification: NotificationItem) => {
    const subject = notification.task.for;

    if (isFaxCommunication(notification.sourceCommunication)) {
      const sourceCommunication = notification.sourceCommunication;
      if (sourceCommunication?.sender) {
        const url = `/Faxing?${FAX_SEARCH_QUERY_PARAM}=${encodeURIComponent(sourceCommunication.sender.display ?? '')}`;
        navigate(url);
      } else if (sourceCommunication?.extension) {
        const originatingNumber = getExtensionValue(sourceCommunication, FAX_ORIGINATING_NUMBER);
        if (originatingNumber && typeof originatingNumber === 'string') {
          const url = `/Faxing?${FAX_SEARCH_QUERY_PARAM}=${encodeURIComponent(originatingNumber)}`;
          navigate(url);
        }
      }
    } else if (subject && isReferenceOf(subject, 'Organization')) {
      // Gated the way the log rows are: for a reader without partner access the Organizations
      // page only bounces them off an access-denied alert, so the row stays where it is.
      const url = getPartnerDrawerUrl(resolveId(subject), hasPartnerAccess);
      if (url) {
        navigate(url);
      }
    } else if (
      notification.sourceCommunication &&
      isUnknownNumberSmsNotification(notification.task, notification.sourceCommunication)
    ) {
      const phone = getExternalNumberFromPhoneCommunication(notification.sourceCommunication as Communication);
      const url = phone ? `/SMS?${SMS_LOGS_PHONE_QUERY_PARAM}=${encodeURIComponent(phone)}` : '/SMS';
      navigate(url);
    } else if (notification.task.groupIdentifier?.value === TaskGroupIdentifier.ORBIT_PROVIDER_TASK) {
      // Clinical task: land directly on the patient's Tasks tab.
      const patientId = subject ? resolveId(subject) : null;
      if (patientId) {
        navigate(`/Patient/${patientId}/timeline?${TIMELINE_INFO_TAB_QUERY_PARAM}=tasks`);
      }
    } else {
      const patientId = subject ? resolveId(subject) : null;

      // Check if Communication.about points to a DiagnosticReport (for abnormal lab results notifications)
      const diagnosticReportRef = notification.sourceCommunication?.about?.find((ref: { reference?: string }) =>
        ref.reference ? isReferenceOf({ reference: ref.reference }, 'DiagnosticReport') : false
      );

      const scrollTo = diagnosticReportRef?.reference
        ? diagnosticReportRef.reference
        : notification.task.focus
          ? getReferenceString(notification.task.focus)
          : null;

      const url = scrollTo
        ? `/Patient/${patientId}/timeline?${TIMELINE_FOCUS_ITEM_QUERY_PARAM}=${encodeURIComponent(scrollTo)}`
        : `/Patient/${patientId}/timeline`;
      navigate(url);
    }
  };

  return { handleRowClick };
}
